import { useEffect, useRef, useState } from 'react';
import { onlineManager, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC, useTRPCClient } from '../../lib/trpc';
import {
  startPoCopyExport,
  downloadNextPoCopyPage,
  type PoCopyExportState,
} from '../../lib/purchase-order-export';
import { fieldErrorOf } from '../../lib/field-error';
import { Field, field } from '../create/Field';
import { useOnline } from '../ui/useConnection';
import { SectionCard } from '../ui/Detail';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { useSession } from '../session';

function saveCsv(file: { filename: string; csv: string }) {
  const url = URL.createObjectURL(
      new Blob([file.csv], { type: 'text/csv;charset=utf-8' }),
    ),
    a = document.createElement('a');
  a.href = url;
  a.download = file.filename;
  document.body.appendChild(a);
  try {
    a.click();
  } finally {
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

// Both CSV callers share this frame's burst budget. Keep only one requested file
// per caller in the chain; a failed or revoked slot must not strand the next one.
let csvDownloadSlot = Promise.resolve(),
  csvDownloadStarted = false;
function sendCsv(file: { filename: string; csv: string }, current: () => boolean) {
  const slot = csvDownloadSlot.then(async () => {
    // Chromium permits ten downloads per >1s burst window: 9 × 150ms > 1s.
    // Initial margin also allows an unrelated earlier burst to expire.
    await new Promise((resolve) => setTimeout(resolve, csvDownloadStarted ? 150 : 1100));
    if (!current()) return false;
    csvDownloadStarted = true;
    saveCsv(file);
    return true;
  });
  csvDownloadSlot = slot.then(
    () => {},
    () => {},
  );
  return slot;
}

type ExportAttempt = { owner: string; generation: number };

/**
 * The honest-terms surfaces (PRD §4.5 F1 + F2), as first-class buttons rather than a
 * support ticket. Two promises made mechanical:
 *
 *  · your data is yours — one click, every table, plain CSV
 *  · leaving is one click — no call, no retention flow, no "are you sure" maze
 *
 * They live TOGETHER on purpose. A shop deciding whether to leave should find the export
 * button in the same place, because the honest version of "you can leave" is "you can
 * leave and take everything with you."
 */
export function YourDataCard({ id }: { id: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const { role, orgId, userId } = useSession();
  const client = useTRPCClient(),
    online = useOnline();
  const copyController = useRef<AbortController | null>(null);
  const [copies, setCopies] = useState<PoCopyExportState | null>(null),
    [copyBusy, setCopyBusy] = useState(false),
    [copyPaused, setCopyPaused] = useState(false),
    [copyError, setCopyError] = useState<string | null>(null);
  const [copyFrom, setCopyFrom] = useState(''),
    [copyTo, setCopyTo] = useState('');
  useEffect(() => {
    copyController.current?.abort();
    copyController.current = null;
    setCopies(null);
    setCopyBusy(false);
    setCopyPaused(false);
    setCopyError(null);
    return () => {
      copyController.current?.abort();
      copyController.current = null;
    };
  }, [orgId, userId, role]);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const exportOwner = `${userId}:${orgId}:${role}`;
  const exportOwnerRef = useRef(exportOwner),
    exportGeneration = useRef(0),
    exportAlive = useRef(true),
    exportPending = useRef<number | null>(null),
    exportMutation = useRef<{ remove(): void } | null>(null);
  // Render invalidates delivery before passive effects, including owner A → B → A.
  if (exportOwnerRef.current !== exportOwner) {
    exportOwnerRef.current = exportOwner;
    exportGeneration.current += 1;
    exportPending.current = null;
  }
  const [exportBusyGeneration, setExportBusyGeneration] = useState<number | null>(null);
  const [exported, setExported] = useState<{
    generation: number;
    files: number;
    rows: number;
  } | null>(null);
  const [exportProgress, setExportProgress] = useState<{
    generation: number;
    sent: number;
    total: number;
  } | null>(null);
  const [exportError, setExportError] = useState<{
    generation: number;
    message: string;
  } | null>(null);
  useEffect(() => {
    exportAlive.current = true;
    return () => {
      exportAlive.current = false;
      exportGeneration.current += 1;
      exportPending.current = null;
      exportMutation.current?.remove();
      exportMutation.current = null;
    };
  }, []);
  const currentExport = (attempt: ExportAttempt) =>
    exportAlive.current &&
    role === 'owner' &&
    exportOwnerRef.current === attempt.owner &&
    exportGeneration.current === attempt.generation;
  const exportBusy = exportBusyGeneration === exportGeneration.current;
  const startExport = () => {
    const attempt: ExportAttempt = {
      owner: exportOwner,
      generation: exportGeneration.current,
    };
    if (
      !currentExport(attempt) ||
      !onlineManager.isOnline() ||
      exportPending.current !== null
    )
      return;
    exportPending.current = attempt.generation;
    setExportBusyGeneration(attempt.generation);
    setExported(null);
    setExportError(null);
    setExportProgress(null);
    // One immutable callback closure per attempt, with the API's original void variables.
    // The shared write guard matches those variables by identity. Drop only our old cache
    // entry before a new account starts; an in-flight reply can still settle, so guard it.
    exportMutation.current?.remove();
    const mutation = qc.getMutationCache().build(
      qc,
      trpc.billing.export.mutationOptions({
        networkMode: 'always',
        retry: false,
        onSuccess: async (data) => {
          if (!currentExport(attempt)) return;
          setExportError(null);
          let sent = 0;
          setExportProgress({
            generation: attempt.generation,
            sent,
            total: data.files.length,
          });
          try {
            for (const file of data.files) {
              if (!(await sendCsv(file, () => currentExport(attempt)))) return;
              if (!currentExport(attempt)) return;
              sent += 1;
              setExportProgress({
                generation: attempt.generation,
                sent,
                total: data.files.length,
              });
            }
          } catch (cause) {
            if (!currentExport(attempt)) return;
            throw new Error(
              `Stopped after ${sent} of ${data.files.length} files were sent to downloads. Check Downloads before starting again.`,
              { cause },
            );
          }
          if (!currentExport(attempt)) return;
          setExported({
            generation: attempt.generation,
            files: data.files.length,
            rows: data.files.reduce((sum, file) => sum + file.rowCount, 0),
          });
        },
        onError: (e) => {
          if (currentExport(attempt))
            setExportError({ generation: attempt.generation, message: e.message });
        },
        onSettled: () => {
          qc.getMutationCache().remove(mutation);
          if (!currentExport(attempt)) return;
          exportPending.current = null;
          exportMutation.current = null;
          setExportBusyGeneration(null);
          setExportProgress(null);
        },
      }),
    );
    exportMutation.current = { remove: () => qc.getMutationCache().remove(mutation) };
    // Active failures are rendered by onError; stale failures cannot alter the new card.
    void mutation.execute(undefined).catch(() => {});
  };

  const cancel = useMutation(
    trpc.billing.cancel.mutationOptions({
      onSuccess: () => {
        setConfirming(false);
        setConfirmText('');
        setError(null);
        void qc.invalidateQueries();
      },
      onError: (e) => setError(e.message),
    }),
  );

  const nextCopies = async () => {
    const attempt: ExportAttempt = {
      owner: exportOwner,
      generation: exportGeneration.current,
    };
    if (copyBusy || !online || !currentExport(attempt)) return;
    const controller = new AbortController();
    copyController.current = controller;
    setCopyBusy(true);
    setCopyPaused(false);
    setCopyError(null);
    try {
      let current = copies;
      if (!current || !current.remainingDocumentIds.length) {
        const manifest = await client.billing.purchaseOrderDocumentManifest.query(
          { ...(copyFrom ? { from: copyFrom } : {}), ...(copyTo ? { to: copyTo } : {}) },
          { signal: controller.signal },
        );
        current = await startPoCopyExport(manifest);
        if (controller.signal.aborted || copyController.current !== controller) return;
        setCopies(current);
      }
      const next = await downloadNextPoCopyPage(
        current,
        (ids, signal) =>
          client.billing.purchaseOrderDocuments.query({ documentIds: ids }, { signal }),
        async (file) => {
          const sent = await sendCsv(
            file,
            () =>
              currentExport(attempt) &&
              !controller.signal.aborted &&
              copyController.current === controller,
          );
          if (!sent) throw new DOMException('Saved-copy export paused.', 'AbortError');
        },
        controller.signal,
      );
      if (copyController.current === controller) setCopies(next);
    } catch (e) {
      if (copyController.current === controller) {
        if (e instanceof Error && e.name === 'AbortError') setCopyPaused(true);
        else
          setCopyError(
            fieldErrorOf(
              e instanceof Error
                ? e.message
                : 'Could not download saved supplier copies.',
            ),
          );
      }
    } finally {
      if (copyController.current === controller) {
        setCopyBusy(false);
        copyController.current = null;
      }
    }
  };
  const pauseCopies = () => {
    copyController.current?.abort();
    setCopyPaused(true);
  };

  if (role !== 'owner') return null;

  return (
    <SectionCard id={id} title="Your data">
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {exportError?.generation === exportGeneration.current ? (
        <Notice tone="danger">{exportError.message}</Notice>
      ) : null}

      <p className="text-sm text-muted-foreground">
        Your customer, job, financial, purchasing, inventory and pricing records download
        as spreadsheets. Saved supplier copies download separately below, with their exact
        PDF bytes in CSV files.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Button disabled={exportBusy || !online} onClick={startExport}>
          {exportBusy
            ? exportProgress?.generation === exportGeneration.current
              ? 'Sending files…'
              : 'Putting it together…'
            : 'Download shop data'}
        </Button>
        {exportBusy && exportProgress?.generation === exportGeneration.current ? (
          <span role="status" className="text-sm text-muted-foreground">
            {exportProgress.sent} of {exportProgress.total} files sent to downloads.
          </span>
        ) : null}
        {exported?.generation === exportGeneration.current ? (
          <span role="status" className="text-sm text-muted-foreground">
            {exported.files} {exported.files === 1 ? 'file' : 'files'} sent to downloads (
            {exported.rows.toLocaleString()} rows).
          </span>
        ) : null}
      </div>

      <div className="mt-4 space-y-3 border-t border-border pt-3">
        <p className="text-sm font-medium">Saved supplier copies</p>
        <p className="text-sm text-muted-foreground">
          Each file contains complete PDF copies as base64 plus their names and checksums.
          Download one page at a time. Newer copies are included only when you start a new
          copy list.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Prepared from (UTC)">
            <input
              className={field}
              type="date"
              value={copyFrom}
              disabled={copyBusy || Boolean(copies)}
              onChange={(e) => setCopyFrom(e.target.value)}
            />
          </Field>
          <Field label="Prepared through (UTC)">
            <input
              className={field}
              type="date"
              value={copyTo}
              disabled={copyBusy || Boolean(copies)}
              onChange={(e) => setCopyTo(e.target.value)}
            />
          </Field>
        </div>
        {copyError ? <Notice tone="danger">{copyError}</Notice> : null}
        {!online ? (
          <Notice tone="warn">
            Go online to download saved copies. Your current copy list is kept.
          </Notice>
        ) : null}
        {copies ? (
          <p role="status" className="text-sm text-muted-foreground">
            {copies.downloadedDocumentIds.length.toLocaleString()} of{' '}
            {copies.manifest.count.toLocaleString()} supplier copies downloaded. Copy list
            captured {new Date(copies.manifest.capturedAt).toLocaleString()}.
            {copyPaused ? ' Paused — resume with this same list.' : ''}
          </p>
        ) : copyPaused ? (
          <p role="status" className="text-sm text-muted-foreground">
            Paused before a copy list was captured. Start again when ready.
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="ghost"
            disabled={copyBusy || !online}
            onClick={() => void nextCopies()}
          >
            {copyBusy
              ? 'Preparing copies…'
              : copies?.remainingDocumentIds.length
                ? copyPaused
                  ? 'Resume copy export'
                  : 'Download next copy page'
                : copies
                  ? 'Start new copy export'
                  : 'Download saved supplier copies'}
          </Button>
          {copyBusy ? (
            <Button variant="ghost" onClick={pauseCopies}>
              Pause copy export
            </Button>
          ) : null}
          {copies && !copyBusy ? (
            <Button
              variant="ghost"
              onClick={() => {
                setCopies(null);
                setCopyPaused(false);
                setCopyError(null);
              }}
            >
              Choose a new copy list
            </Button>
          ) : null}
        </div>
      </div>

      <div className="mt-3 border-t border-border pt-3">
        <p className="text-sm font-medium">Leaving</p>
        <p className="mt-0.5 text-sm text-muted-foreground">
          You can stop whenever you like, from right here. No phone call, no form, nobody
          trying to talk you out of it. Download your data first — the button is right
          above.
        </p>
        {!confirming ? (
          <Button variant="ghost" className="mt-2" onClick={() => setConfirming(true)}>
            Cancel my subscription
          </Button>
        ) : (
          <div className="mt-2 space-y-2">
            {/* One typed word, not a maze. Enough that nobody cancels by mis-clicking,
                not so much that it becomes the retention flow this promises not to be. */}
            <Notice tone="warn">
              Type <strong>CANCEL</strong> to confirm. This takes effect immediately.
            </Notice>
            <div className="flex flex-wrap gap-2">
              <input
                className="min-w-0 flex-1 rounded-[var(--radius-sm)] border border-border bg-surface px-2.5 py-1.5 text-sm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                aria-label="Type CANCEL to confirm"
                placeholder="CANCEL"
                autoFocus
              />
              <Button
                variant="danger"
                disabled={
                  confirmText.trim().toUpperCase() !== 'CANCEL' || cancel.isPending
                }
                onClick={() => cancel.mutate()}
              >
                {cancel.isPending ? 'Cancelling…' : 'Cancel it'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setConfirming(false);
                  setConfirmText('');
                }}
              >
                Never mind
              </Button>
            </div>
          </div>
        )}
      </div>
    </SectionCard>
  );
}
